import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { authAPI, businessAPI, accountingAPI, contactAPI } from '../services/api';
import { getApiErrorMessage } from '../utils/authValidation';
import { tearDownVoiceForScope } from '../voice/voiceLifecycle';

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  status: string;
  is_super_admin?: boolean;
  created_at: string;
  created_by_first_name?: string;
  created_by_last_name?: string;
  role: string;
  business_role?: string | null;
  business_id?: number | null;
  is_active: boolean;
}

interface PermissionGrant {
  resource: string;
  action: string;
}

interface Business {
  id: number;
  name: string;
  email: string;
  phone: string;
  address: string;
  country_code: string;
  base_currency_code: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface Account {
  id: number;
  name: string;
  type: string;
  code: string;
}

interface Contact {
  id: number;
  company_name: string;
  contact_name: string;
  email: string;
  phone: string;
  contact_type: string;
}

interface HybridAuthContextType {
  user: User | null;
  business: Business | null;
  accounts: Account[];
  contacts: Contact[];
  permissions: PermissionGrant[];
  loading: boolean;
  error: string | null;
  loginError: string | null;
  authRateLimited: boolean;
  authInitialized: boolean;
  isAuthenticated: boolean;
  isLoggingOut: boolean;
  login: (email: string, password: string, businessId?: string) => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  register: (userData: any, businessData?: any) => Promise<void>;
  logout: () => void;
  refreshData: (force?: boolean) => Promise<void>;
  refreshSharedData: () => Promise<void>;
  switchBusiness: (businessId: string) => Promise<void>;
  createBusiness: (businessData: any) => Promise<void>;
  hasPermission: (resource: string, action?: string) => boolean;
}

const HybridAuthContext = createContext<HybridAuthContextType | undefined>(undefined);

export const useHybridAuth = () => {
  const context = useContext(HybridAuthContext);
  if (context === undefined) {
    throw new Error('useHybridAuth must be used within a HybridAuthProvider');
  }
  return context;
};

const MIN_AUTH_REFRESH_MS = 8000;
const AUTH_RATE_LIMIT_BACKOFF_MS = 5 * 60 * 1000;
const AUTH_RATE_LIMIT_KEY = 'authRateLimitedUntil';

const getAuthRateLimitedUntil = (): number => {
  try {
    const raw = sessionStorage.getItem(AUTH_RATE_LIMIT_KEY);
    const parsed = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
};

const setAuthRateLimitedUntil = (backoffMs: number) => {
  try {
    sessionStorage.setItem(AUTH_RATE_LIMIT_KEY, String(Date.now() + backoffMs));
  } catch {
    // ignore storage errors
  }
};

const clearAuthRateLimitedUntil = () => {
  try {
    sessionStorage.removeItem(AUTH_RATE_LIMIT_KEY);
  } catch {
    // ignore storage errors
  }
};

const AUTH_RATE_LIMIT_MESSAGE =
  'Too many sign-in attempts. Please wait a few minutes and try again.';

const isAuthRateLimitRequest = (err: { config?: { url?: string } }) => {
  const url = err?.config?.url || '';
  return url.includes('/auth/login') || url.includes('/auth/me') || url.includes('/auth/profile');
};

const hasStoredAuthToken = () => {
  try {
    return Boolean(localStorage.getItem('token'));
  } catch {
    return false;
  }
};

export const HybridAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [permissions, setPermissions] = useState<PermissionGrant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [authRateLimited, setAuthRateLimited] = useState(
    () => Date.now() < getAuthRateLimitedUntil()
  );
  const [authInitialized, setAuthInitialized] = useState(
    () => !hasStoredAuthToken() || Date.now() < getAuthRateLimitedUntil()
  );
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const fetchInFlightRef = useRef<Promise<void> | null>(null);
  const lastFetchAtRef = useRef(0);
  const rateLimitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const syncAuthRateLimitState = useCallback((showLoginMessage = false) => {
    if (rateLimitTimerRef.current) {
      clearTimeout(rateLimitTimerRef.current);
      rateLimitTimerRef.current = null;
    }

    const remaining = getAuthRateLimitedUntil() - Date.now();
    if (remaining <= 0) {
      setAuthRateLimited(false);
      clearAuthRateLimitedUntil();
      return;
    }

    setAuthRateLimited(true);
    if (showLoginMessage) {
      setLoginError((prev) => prev ?? AUTH_RATE_LIMIT_MESSAGE);
    }

    rateLimitTimerRef.current = setTimeout(() => {
      rateLimitTimerRef.current = null;
      clearAuthRateLimitedUntil();
      setAuthRateLimited(false);
      setLoginError((prev) => (prev === AUTH_RATE_LIMIT_MESSAGE ? null : prev));
    }, remaining);
  }, []);

  useEffect(() => {
    syncAuthRateLimitState(true);
    return () => {
      if (rateLimitTimerRef.current) {
        clearTimeout(rateLimitTimerRef.current);
        rateLimitTimerRef.current = null;
      }
    };
  }, [syncAuthRateLimitState]);

  const loadAccountsAndContacts = useCallback(async (businessId: number | null) => {
    // Super admins (and users without a business) have no chart of accounts —
    // calling /accounting/accounts without business_id returns 400.
    if (!businessId) {
      setAccounts([]);
      setContacts([]);
      return;
    }

    try {
      const [accountsResponse, contactsResponse] = await Promise.all([
        accountingAPI.getAccounts({ business_id: businessId }),
        contactAPI.getAll(businessId),
      ]);

      if (accountsResponse.data.success) {
        const rawAccounts = accountsResponse.data.data.accounts || [];
        setAccounts(rawAccounts.map((account: any) => ({
          id: account.id,
          name: account.account_name || account.name,
          type: account.account_type || account.type,
          code: account.account_code || account.code,
        })));
      }

      if (contactsResponse.data.success) {
        const rawContacts = contactsResponse.data.data.contacts || [];
        setContacts(rawContacts.map((contact: any) => ({
          id: contact.id,
          company_name: contact.company_name || '',
          contact_name: contact.contact_name || '',
          email: contact.email || '',
          phone: contact.phone || '',
          contact_type: contact.contact_type || '',
        })));
      }
    } catch (dataError) {
      console.warn('Could not fetch shared data:', dataError);
    }
  }, []);

  const fetchUserData = useCallback(async (options?: { force?: boolean }) => {
    const force = options?.force === true;
    const now = Date.now();
    if (now < getAuthRateLimitedUntil()) {
      syncAuthRateLimitState();
      return;
    }
    setAuthRateLimited(false);

    if (!force && now - lastFetchAtRef.current < MIN_AUTH_REFRESH_MS) {
      return;
    }

    if (fetchInFlightRef.current) {
      return fetchInFlightRef.current;
    }

    const run = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          return;
        }

        const userResponse = await authAPI.getMe();
        if (userResponse.data.success) {
          const userData = userResponse.data.data.user;
          const permissionData = userResponse.data.data.permissions || [];
          setUser(userData);
          setPermissions(permissionData);
          setIsAuthenticated(true);
          lastFetchAtRef.current = Date.now();
          // Persist user so scoped voice storage (voice:<userId>:<businessId>:*)
          // can resolve a userId; without this, chat history saves silently no-op.
          try {
            localStorage.setItem('user', JSON.stringify(userData));
          } catch {
            /* ignore storage errors */
          }

          let businessId: number | null = null;
          try {
            const userBusinessesResponse = await businessAPI.getUserBusinesses();
            if (userBusinessesResponse.data.success && userBusinessesResponse.data.data.businesses.length > 0) {
              const businesses = userBusinessesResponse.data.data.businesses;
              const currentBusiness = businesses.find((b: any) => b.email === userData.email)
                || businesses.find((b: any) => b.role === 'administrator' || b.role === 'owner')
                || businesses[0];
              setBusiness(currentBusiness);
              businessId = currentBusiness.id;
              localStorage.setItem('businessId', String(currentBusiness.id));

              // Permissions depend on business membership role — refresh after business is known.
              const refreshedMe = await authAPI.getMe(currentBusiness.id);
              if (refreshedMe.data.success) {
                setUser(refreshedMe.data.data.user);
                setPermissions(refreshedMe.data.data.permissions || []);
              }
            }
          } catch (businessError) {
            console.warn('Could not fetch user businesses:', businessError);
          }

          await loadAccountsAndContacts(businessId);
        } else {
          throw new Error(userResponse.data.error || 'Failed to fetch user data');
        }
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 429 && isAuthRateLimitRequest(err)) {
          console.warn('Auth refresh rate-limited; backing off before retrying.');
          setAuthRateLimitedUntil(AUTH_RATE_LIMIT_BACKOFF_MS);
          syncAuthRateLimitState(!user);
          if (user) {
            setError('Too many requests. Please wait a few minutes before refreshing.');
          } else {
            setLoginError(AUTH_RATE_LIMIT_MESSAGE);
          }
          return;
        }

        console.error('Error fetching data:', err);
        if (status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('businessId');
          localStorage.removeItem('user');
          setIsAuthenticated(false);
          setUser(null);
          setBusiness(null);
          setAccounts([]);
          setContacts([]);
          return;
        }
        if (!user) {
          return;
        }
        setError(err.message || 'Failed to fetch user data');
      } finally {
        setLoading(false);
        fetchInFlightRef.current = null;
      }
    };

    fetchInFlightRef.current = run();
    return fetchInFlightRef.current;
  }, [loadAccountsAndContacts, syncAuthRateLimitState]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || Date.now() < getAuthRateLimitedUntil()) {
      setAuthInitialized(true);
      return;
    }

    setAuthInitialized(false);
    let cancelled = false;
    void Promise.resolve(fetchUserData({ force: true })).finally(() => {
      if (!cancelled) {
        setAuthInitialized(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fetchUserData]);

  const login = async (email: string, password: string, businessId?: string) => {
    if (Date.now() < getAuthRateLimitedUntil()) {
      syncAuthRateLimitState(true);
      return;
    }

    setLoading(true);
    setError(null);
    setLoginError(null);
    
    try {
      const { data } = await authAPI.login(email, password);
      localStorage.setItem('token', data.data.token);
      clearAuthRateLimitedUntil();
      setAuthRateLimited(false);
      setLoginError(null);
      await fetchUserData({ force: true });
    } catch (err: any) {
      const status = err?.response?.status;
      const message = err?.response?.data?.error || (err instanceof Error ? err.message : 'Login failed');
      if (status === 429) {
        setAuthRateLimitedUntil(AUTH_RATE_LIMIT_BACKOFF_MS);
        syncAuthRateLimitState(true);
        setLoginError(AUTH_RATE_LIMIT_MESSAGE);
      } else {
        setLoginError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const loginWithToken = async (token: string) => {
    if (!token) {
      throw new Error('Missing authentication token');
    }

    setLoading(true);
    setError(null);
    setLoginError(null);

    try {
      localStorage.setItem('token', token);
      clearAuthRateLimitedUntil();
      setAuthRateLimited(false);
      await fetchUserData({ force: true });

      const stored = localStorage.getItem('token');
      if (!stored) {
        throw new Error('Google sign-in failed');
      }

      // Confirm session via /me — fetchUserData swallows some errors
      const me = await authAPI.getMe();
      if (!me.data?.success || !me.data?.data?.user) {
        throw new Error(me.data?.error || 'Google sign-in failed');
      }
      setUser(me.data.data.user);
      setPermissions(me.data.data.permissions || []);
      setIsAuthenticated(true);
      try {
        localStorage.setItem('user', JSON.stringify(me.data.data.user));
      } catch {
        /* ignore */
      }
    } catch (err: any) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setIsAuthenticated(false);
      setUser(null);
      const message = err?.response?.data?.error || (err instanceof Error ? err.message : 'Google sign-in failed');
      setLoginError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: any, businessData?: any) => {
    try {
      setLoading(true);
      setError(null);

      // Create user first
      const userResponse = await authAPI.register({
        email: userData.email,
        password: userData.password,
        first_name: userData.first_name,
        last_name: userData.last_name,
        role: 'admin' // First user becomes admin of their business
      });

      if (!userResponse.data.success) {
        throw new Error(userResponse.data.error || 'Failed to create user');
      }

      // Store token
      localStorage.setItem('token', userResponse.data.data.token);

      // Create business if provided
      if (businessData) {
        const businessResponse = await businessAPI.create(businessData);
        
        if (!businessResponse.data.success) {
          throw new Error(businessResponse.data.error || 'Failed to create business');
        }

        const newBusiness = businessResponse.data.data.business;
        setBusiness(newBusiness);
      }

      // Refresh user data
      await fetchUserData({ force: true });
    } catch (err: unknown) {
      const message = getApiErrorMessage(err, 'Registration failed');
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setIsLoggingOut(true);
    localStorage.setItem('isLoggingOut', 'true');

    // Tear down voice before clearing auth so pending mutations cannot execute
    const outgoingScope =
      user?.id != null && business?.id != null
        ? { userId: String(user.id), businessId: String(business.id) }
        : null;
    tearDownVoiceForScope(outgoingScope, 'logout');

    localStorage.removeItem('token');
    localStorage.removeItem('businessId');
    localStorage.removeItem('user');
    setUser(null);
    setBusiness(null);
    setAccounts([]);
    setContacts([]);
    setPermissions([]);
    setIsAuthenticated(false);
    setAuthInitialized(true);
    setError(null);
    setLoginError(null);

    // Reset logging out flag after a short delay to allow state changes to complete
    setTimeout(() => {
      setIsLoggingOut(false);
      localStorage.removeItem('isLoggingOut');
    }, 500);
  };

  const refreshData = async (force?: boolean) => {
    await fetchUserData({ force });
  };

  const refreshSharedData = async () => {
    const storedId = parseInt(localStorage.getItem('businessId') || '', 10);
    const businessId = business?.id ?? (Number.isFinite(storedId) ? storedId : null);
    await loadAccountsAndContacts(businessId);
  };

  const switchBusiness = async (businessId: string) => {
    try {
      setLoading(true);

      // Tear down outgoing business voice state before flipping businessId
      if (user?.id != null && business?.id != null) {
        tearDownVoiceForScope(
          { userId: String(user.id), businessId: String(business.id) },
          'business-switch'
        );
      }

      // Fetch business data
      const businessResponse = await businessAPI.getById(parseInt(businessId));
      if (businessResponse.data.success) {
        setBusiness(businessResponse.data.data.business);
        localStorage.setItem('businessId', businessId);

        // Refresh role permissions for the newly selected business
        try {
          const me = await authAPI.getMe(businessId);
          if (me.data.success) {
            setUser(me.data.data.user);
            setPermissions(me.data.data.permissions || []);
          }
        } catch (permErr) {
          console.warn('Could not refresh permissions after business switch:', permErr);
        }
        
        // Fetch contacts for this business
        const contactsResponse = await contactAPI.getAll(parseInt(businessId));
        if (contactsResponse.data.success) {
          const rawContacts = contactsResponse.data.data.contacts || [];
          const mappedContacts = rawContacts.map((contact: any) => ({
            id: contact.id,
            company_name: contact.company_name || '',
            contact_name: contact.contact_name || '',
            email: contact.email || '',
            phone: contact.phone || '',
            contact_type: contact.contact_type || ''
          }));
          setContacts(mappedContacts);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to switch business');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createBusiness = async (businessData: any) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await businessAPI.create(businessData);
      
      if (response.data.success) {
        const newBusiness = response.data.data.business;
        setBusiness(newBusiness);
        // Switch to the new business
        await switchBusiness(newBusiness.id.toString());
      } else {
        throw new Error(response.data.error || 'Failed to create business');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create business');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const hasPermissionFn = useCallback(
    (resource: string, action: string = 'read') => {
      if (user?.is_super_admin) return true;
      return permissions.some((p) => p.resource === resource && p.action === action);
    },
    [permissions, user?.is_super_admin]
  );

  const contextValue: HybridAuthContextType = {
    user,
    business,
    accounts,
    contacts,
    permissions,
    loading,
    error,
    loginError,
    authRateLimited,
    authInitialized,
    isAuthenticated,
    isLoggingOut,
    login,
    loginWithToken,
    register,
    logout,
    refreshData,
    refreshSharedData,
    switchBusiness,
    createBusiness,
    hasPermission: hasPermissionFn,
  };

  return (
    <HybridAuthContext.Provider value={contextValue}>
      {children}
    </HybridAuthContext.Provider>
  );
};

export default HybridAuthContext;
